<div
  class="case-details-page container-fluid"
  :class="{ 'people-panel-is-collapsed': people_panel_collapsed }"
>
  <div class="case-details-shell d-flex flex-column mx-auto position-relative">
    <blocking-loader-overlay
      :show="page_loading"
      :message="page_loading_message"
      :detail="page_loading_detail"
    />
    <section class="case-details-page-intro">
      <div class="case-details-breadcrumb">
        <div class="row g-1 align-items-start">
          <div class="col">
            <div class="text-uppercase d-flex align-items-center">
              <span>Cases</span>
              <span>/</span>
              <span>{{ display_case_code }}</span>
            </div>
            <h1 class="entity-page-title lh-sm mt-2">Case details</h1>
          </div>
        </div>
      </div>
    </section>

    <section class="row g-3 align-items-start case-details-top-grid">
      <div class="col-12 case-details-main-column">
        <div class="case-details-header-card bg-white">
        <div class="d-flex flex-column flex-xl-row justify-content-between gap-3 mb-5">
          <div>
            <div class="case-details-subtitle secondary-color">{{ display_case_meta }}</div>
            <h4 class="case-details-title secondary-color fw-bold mb-2 mt-2">{{ display_case_title }}</h4>
          </div>

          <div class="case-details-header-actions d-flex align-items-start gap-2 justify-content-xl-end">
            <span v-if="display_sla_badge.label" class="badge rounded-3 fw-normal small px-3 py-2 case-details-overdue-badge" :class="display_sla_badge.class">{{ display_sla_badge.label }}</span>
            <button type="button" class="btn btn-primary-dark" @click="open_reassign_modal">Reassign</button>
            <button type="button" class="btn btn-primary-dark" @click="open_decision_modal">Decide</button>
          </div>
        </div>

        <div class="row g-4 pt-4 case-details-summary-grid">
          <div v-for="item in display_summary_rows" :key="item.label" class="col-12 col-sm-6 col-xl-3">
            <div class="text-uppercase text-x fw-bold secondary-color mb-2">{{ item.label }}</div>

            <span v-if="item.is_badge" class="badge rounded-pill fw-normal" :class="item.badge_class">{{ item.value }}</span>

            <div v-else-if="item.is_status" class="d-flex align-items-center gap-2">
              <span class="status-dot-sm rounded-circle d-inline-block" :class="item.dot_class"></span>
              <span class="fw-semibold text-dark">{{ item.value }}</span>
            </div>

            <div v-else class="fw-semibold text-dark">{{ item.value }}</div>
          </div>
        </div>
      </div>
      </div>

      <div class="col-12 case-details-people-column">
        <aside
          class="case-details-people-card bg-white"
          :class="{ 'is-collapsed': people_panel_collapsed }"
        >
          <template v-if="!people_panel_collapsed">
            <div class="d-flex align-items-center justify-content-between gap-2 mb-4">
              <div class="text-uppercase fw-bold secondary-color">People on this case</div>
              <button
                type="button"
                class="case-details-people-toggle fs-4"
                aria-label="Collapse people panel"
                @click="people_panel_collapsed = true"
              >
                &raquo;
              </button>
            </div>
            <div class="d-flex flex-column gap-4">
              <div v-for="person in display_people" :key="person.key" class="d-flex align-items-center gap-3">
                <span class="avatar-circle text-white fw-bold case-details-person-avatar" :class="person.class">{{ person.initials }}</span>
                <div>
                  <div class="fw-semibold text-dark">{{ person.name }}</div>
                  <div class="secondary-color small">{{ person.role }}</div>
                </div>
              </div>
            </div>
          </template>

          <button
            v-else
            type="button"
            class="case-details-people-collapsed"
            aria-label="Expand people panel"
            @click="people_panel_collapsed = false"
          >
            <span class="case-details-people-chevron fs-2">&lsaquo;</span>
            <span class="case-details-people-vertical-label">People &middot; {{ display_people.length }}</span>
            <span class="case-details-people-avatar-stack">
              <span
                v-for="person in display_people"
                :key="`collapsed-${person.key}`"
                class="avatar-circle text-white fw-bold case-details-person-avatar"
                :class="person.class"
              >
                {{ person.initials }}
              </span>
            </span>
          </button>
        </aside>
      </div>
    </section>

    <section class="row g-3 mt-0">
      <div class="col-12 case-details-main-column">
        <div class="case-details-lifecycle-card bg-white">
          <div class="case-details-lifecycle-header">
            <div class="text-uppercase fw-bold text-x secondary-color">Case lifecycle</div>
            <div class="case-details-lifecycle-summary">{{ lifecycle_stage_summary }}</div>
          </div>

          
          <div v-if="!loading_decision_workflow && !lifecycle_steps.length" class="case-details-lifecycle-empty secondary-color">
            No workflow statuses are configured for this case.
          </div>

          <div v-else-if="!loading_decision_workflow" class="case-details-lifecycle-track" role="list" aria-label="Case lifecycle">
            <div
              v-for="(step, index) in lifecycle_steps"
              :key="step.id || step.name"
              class="case-details-lifecycle-step"
              :class="`is-${step.state}`"
              role="listitem"
            >
              <div class="case-details-lifecycle-node-row">
                <span
                  v-if="index > 0"
                  class="case-details-lifecycle-connector is-before"
                  :class="{ 'is-complete': ['complete', 'current'].includes(step.state) }"
                ></span>
                <span class="case-details-lifecycle-node">
                  <i v-if="step.marker === 'check'" class="bi bi-check-lg"></i>
                  <span v-else>{{ step.marker }}</span>
                </span>
                <span
                  v-if="index < lifecycle_steps.length - 1"
                  class="case-details-lifecycle-connector is-after"
                  :class="{ 'is-complete': step.state === 'complete' }"
                ></span>
              </div>
              <div class="case-details-lifecycle-name">{{ step.name }}</div>
              <div class="case-details-lifecycle-helper">{{ step.helper }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="row g-3 mt-0">
      <div class="col-12 case-details-main-column">
        <div class="case-details-facts-card bg-white">
          <div>
            <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2">
              <div class="text-uppercase fw-bold text-x secondary-color mb-4">Case facts</div>
             </div>

            <div v-if="loading_verification_checks" class="case-details-doc-message rounded-3 px-3 py-2 mb-4">
              Loading configured verification checks...
            </div>
            <div v-if="verification_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-4">
              {{ verification_error }}
            </div>
            <div
              v-if="has_case_form && !loading_verification_checks && !verification_error && !verification_checks.length"
              class="case-details-doc-message rounded-3 px-3 py-2 mb-4"
            >
              No automatic verification services are configured for this case type.
            </div>
            <case-verification-card
              v-for="check in verification_checks"
              :key="check.id"
              :rows="check.rows"
              :last-run="check.last_run"
              :running="verification_check_is_running(check)"
              :title="check.name"
              :description="check.description"
              :status="check.status"
              :status-tone="check.tone"
              :has-run="check.has_run"
              :runnable="check.runnable"
              :message="check.message"
              unavailable-message="This verification is not mapped to a submitted form for this case type."
              @rerun="run_case_verification(check)"
            />

            <div class="row g-4 bg-white rounded-4 p-3 mt-1">
              <div v-if="fact_edit_error" class="col-12">
                <div class="case-details-doc-message is-error rounded-3 px-3 py-2">{{ fact_edit_error }}</div>
              </div>
              <div v-if="fact_edit_success" class="col-12">
                <div class="case-details-doc-message is-success rounded-3 px-3 py-2">{{ fact_edit_success }}</div>
              </div>
              <div v-if="case_form_submission_error" class="col-12">
                <div class="case-details-doc-message is-error rounded-3 px-3 py-2">{{ case_form_submission_error }}</div>
              </div>
              <div v-if="case_form_submission_success" class="col-12">
                <div class="case-details-doc-message is-success rounded-3 px-3 py-2">{{ case_form_submission_success }}</div>
              </div>
              <div v-if="!loading_case_facts && !has_case_form" class="col-12">
                <div class="case-details-doc-card rounded-4 p-3">
                  <div class="fw-semibold text-dark mb-1">No form configured</div>
                  <div class="small secondary-color">
                    This case type does not have a form assigned to it. Case facts cannot be submitted until a form is configured for the case type.
                  </div>
                </div>
              </div>
              <div v-else-if="!loading_case_facts && !has_case_facts" class="col-12">
                <div class="case-details-doc-card rounded-4 p-3">
                  <div class="fw-semibold text-dark mb-1">No case facts found</div>
                  <div class="small secondary-color">This section stays empty until case facts are submitted.</div>
                </div>
              </div>
              <div v-for="column in display_fact_columns" :key="column.title" class="col-12 col-xl-6">
                <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div class="text-uppercase text-xxs secondary-color fw-semibold">{{ column.title }}</div>
                  <button
                    v-if="can_edit_fact_section(column) && !is_editing_fact_section(column)"
                    type="button"
                    class="case-details-fact-edit btn btn-sm d-inline-flex align-items-center gap-2"
                    @click="begin_fact_section_edit(column)"
                  >
                    <i class="bi bi-pencil"></i>
                    <span>{{ fact_section_needs_details(column) ? 'Fill details' : 'Edit' }}</span>
                  </button>
                </div>
                <div class="case-details-fact-table">
                  <div v-for="fact in column.items" :key="fact_row_key(fact)" class="case-details-fact-row">
                    <div class="case-details-fact-label secondary-color">
                      <span>{{ fact.label }}</span><span v-if="fact.is_mandatory" class="case-fact-required-marker">*</span>
                    </div>
                    <div class="case-details-fact-content">
                      <template v-if="is_editing_fact_section(column) && can_edit_fact(fact)">
                        <select
                          v-if="is_select_fact(fact)"
                          :value="editing_fact_value(fact)"
                          class="form-select case-details-fact-input"
                          @change="update_fact_value(fact, $event.target.value)"
                        >
                          <option value="">Select an option</option>
                          <option v-for="option in fact.options" :key="option" :value="option">{{ option }}</option>
                        </select>

                        <select
                          v-else-if="is_multi_select_fact(fact)"
                          :value="editing_fact_values(fact)"
                          class="form-select case-details-fact-input"
                          multiple
                          @change="update_fact_multi_value(fact, $event.target.selectedOptions)"
                        >
                          <option v-for="option in fact.options" :key="option" :value="option">{{ option }}</option>
                        </select>

                        <div v-else-if="is_checkbox_fact(fact)" class="case-details-fact-options">
                          <label v-for="option in fact.options" :key="option" class="form-check">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :value="option"
                              :checked="editing_fact_values(fact).includes(option)"
                              @change="update_fact_checkbox_value(fact, option, $event.target.checked)"
                            >
                            <span class="form-check-label">{{ option }}</span>
                          </label>
                        </div>

                        <div v-else-if="is_radio_fact(fact)" class="case-details-fact-options">
                          <label v-for="option in fact.options" :key="option" class="form-check">
                            <input
                              class="form-check-input"
                              type="radio"
                              :name="fact_row_key(fact)"
                              :value="option"
                              :checked="editing_fact_value(fact) === option"
                              @change="update_fact_value(fact, $event.target.value)"
                            >
                            <span class="form-check-label">{{ option }}</span>
                          </label>
                        </div>

                        <textarea
                          v-else-if="is_long_text_fact(fact)"
                          :value="editing_fact_value(fact)"
                          class="form-control case-details-fact-input"
                          rows="3"
                          @input="update_fact_value(fact, $event.target.value)"
                        ></textarea>

                        <input
                          v-else-if="is_file_fact(fact)"
                          type="file"
                          :accept="accepted_file_types_for_fact(fact)"
                          class="form-control case-details-fact-input"
                          @change="update_fact_file_value(fact, $event.target.files)"
                        >

                        <input
                          v-else
                          :value="editing_fact_value(fact)"
                          :type="input_type_for_fact(fact)"
                          :step="input_step_for_fact(fact)"
                          class="form-control case-details-fact-input"
                          @input="update_fact_value(fact, $event.target.value)"
                        >
                      </template>

                      <template v-else>
                        <div class="case-details-fact-value secondary-color" :class="`is-${fact.tone}`">{{ fact.value }}</div>
                      </template>
                    </div>
                  </div>
                </div>
                <div v-if="is_editing_fact_section(column)" class="case-details-fact-actions mt-3">
                  <button type="button" class="btn btn-primary-dark rounded-small px-3" :disabled="is_saving_fact_section(column)" @click="save_fact_section_edit(column)">
                    {{ is_saving_fact_section(column) ? 'Saving...' : 'Save section' }}
                  </button>
                  <button type="button" class="btn btn-light rounded-small border px-3" :disabled="is_saving_fact_section(column)" @click="cancel_fact_section_edit">
                    Cancel
                  </button>
                </div>
              </div>
            </div>

            <div v-if="has_case_form && case_form_facts.length" class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mt-4 px-3">
              <div class="small secondary-color">
                Save each section as you work. Submit the completed form once to calculate or update the case risk.
                <span v-if="missing_required_case_form_facts.length" class="d-block text-danger fw-semibold mt-1">
                  {{ missing_required_case_form_facts.length }} required field{{ missing_required_case_form_facts.length === 1 ? '' : 's' }} remaining.
                </span>
              </div>
              <button
                type="button"
                class="btn btn-primary-dark rounded-small px-4 flex-shrink-0"
                :disabled="!can_submit_case_form"
                @click="submit_case_form_for_risk"
              >
                {{ submitting_case_form ? 'Submitting...' : 'Submit form and run risk' }}
              </button>
            </div>

            <div v-if="display_signal_pills.length || display_signal_timeline.length" class="mt-4">
              <div class="text-uppercase text-x fw-bold secondary-color fw-semibold mb-3">Related signals</div>
              <div class="d-flex flex-wrap gap-2 mb-3">
                <span v-for="pill in display_signal_pills" :key="pill" class="badge rounded-pill fw-normal case-details-signal-pill">{{ pill }}</span>
              </div>

              <div class="row g-2">
                <div v-for="item in display_signal_timeline" :key="`${item.meta}-${item.title}`" class="col-12 col-md-6 col-xl-2">
                  <article class="case-details-timeline-card rounded-4 p-3" :class="{ 'is-alert': item.alert }">
                    <div class="text-uppercase text-xxs secondary-color mb-2">{{ item.meta }}</div>
                    <div class="fw-semibold text-dark">{{ item.title }}</div>
                  </article>
                </div>
              </div>
            </div>
        </div>
      </div>
      </div>
    </section>

    <section class="row g-3 mt-0">
      <div class="col-12 case-details-main-column">
        <div class="case-details-documents-card bg-white">
          <div class="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div class="text-uppercase text-x fw-bold secondary-color">Documents</div>
            <div class="text-uppercase text-xxs secondary-color">{{ display_document_count }} files</div>
          </div>

          <div v-if="loading_documents" class="small secondary-color py-3">Loading documents...</div>
          <div v-if="documents_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ documents_error }}</div>
          <div v-if="documents_success" class="case-details-doc-message is-success rounded-3 px-3 py-2 mb-3">{{ documents_success }}</div>

          <div v-if="!loading_documents">
            <div v-if="!display_documents.length" class="case-details-doc-card rounded-4 p-3">
              <div class="fw-semibold text-dark mb-1">No documents found</div>
              <div class="small secondary-color">Uploaded files and attachments will appear here.</div>
            </div>

            <div v-else class="case-details-document-list">
              <article v-for="doc in display_documents" :key="doc.id || doc.name" class="case-details-document-row">
                <span class="case-details-document-icon">{{ doc.extension }}</span>
                <div class="case-details-document-main">
                  <div class="case-details-document-title">{{ doc.name }}</div>
                  <div class="case-details-document-meta-row">
                    <div class="case-details-document-meta">{{ doc.meta }}</div>
                    <span
                      class="badge rounded-pill fw-normal case-details-document-status"
                      :class="doc.verification_state === 'verified'
                        ? 'bg-success-subtle text-success-emphasis'
                        : doc.verification_state === 'rejected'
                          ? 'bg-danger-subtle text-danger-emphasis'
                          : 'bg-warning-subtle text-warning-emphasis'"
                    >
                      {{ doc.verification_label }}
                    </span>
                  </div>
                </div>
                <div class="case-details-document-size">{{ doc.size }}</div>
                <button type="button" class="case-details-document-open btn btn-light border rounded-pill px-3 py-2" @click="open_document(doc)">
                  Open
                </button>
              </article>
            </div>
          </div>

          <div class="case-details-upload-area">
            <input ref="document_upload_input" type="file" class="d-none" @change="upload_document">
            <button
              type="button"
              class="btn btn-light rounded-small border px-3 case-details-upload-btn"
              :disabled="uploading_document || !selected_application_id"
              @click="choose_document_file"
            >
              {{ uploading_document ? `Uploading ${upload_progress}%` : '+ Upload document' }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <section class="row g-3 mt-0 align-items-start case-details-lower-grid">
      <div class="col-12 case-details-main-column">
        <div class="case-details-outcome-card bg-white p-4 mb-3">
          <div class="d-flex flex-column flex-lg-row align-items-lg-start justify-content-between gap-3 mb-3">
            <div class="text-uppercase text-x fw-bold secondary-color">Risk &amp; recommended outcome</div>
          </div>

          <div class="row g-4 align-items-stretch case-details-outcome-layout">
            <div class="col-12 col-xl-6 case-details-outcome-summary">
              <div class="case-details-risk-decision mt-4">
            <div class="case-details-risk-decision-rail" role="list" aria-label="Risk aligned decision guidance">
              <div
                v-for="option in risk_decision_options"
                :key="option.key"
                class="case-details-risk-decision-option"
                :class="{ 'is-active': option.active }"
                role="listitem"
              >
                <span class="case-details-risk-decision-node"></span>
                <span class="case-details-risk-decision-label">{{ option.label }}</span>
              </div>
            </div>
            <div class="case-details-risk-decision-copy secondary-color">
              {{ risk_decision_description }}
            </div>
          </div>
            </div>

            <div class="col-12 col-xl-6">
              <div class="case-details-risk-panel p-4 h-100">
                <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
                  <div class="text-uppercase text-x text-white opacity-75">Risk score</div>
                  <div v-if="loading_risk_panel" class="text-uppercase text-xxs text-white opacity-75">Loading</div>
                </div>

                <div v-if="has_risk_panel">
                  <div class="case-details-risk-score mb-3">{{ risk_panel.score }}<span>/100</span></div>
                  <div v-if="display_risk_band" class="badge rounded-2 fw-normal case-details-risk-badge mb-3">{{ display_risk_band }}</div>

                  <div class="progress case-details-risk-progress mb-4">
                    <div class="progress-bar" role="progressbar" :style="{ width: risk_progress_width }"></div>
                  </div>

                  <div class="text-uppercase text-x text-white opacity-75 mb-3">Risk matrix &middot; key drivers</div>
                  <div v-if="!display_risk_drivers.length" class="text-white small opacity-75">
                    No key drivers were returned by the backend for this score.
                  </div>
                  <div v-else class="d-flex flex-column">
                    <div v-for="driver in display_risk_drivers" :key="driver.label" class="case-details-risk-driver">
                      <div class="d-flex align-items-center justify-content-between gap-3">
                        <div class="d-flex align-items-center gap-3">
                          <span class="case-details-risk-delta">{{ driver.delta }}</span>
                          <span class="min-w-0">
                            <span class="d-block text-white">{{ driver.question }}</span>
                            <span class="d-block text-white small opacity-75">{{ driver.answer }}</span>
                          </span>
                        </div>
                        <span class="badge rounded-2 fw-normal case-details-risk-driver-tag" :class="{ 'is-down': driver.direction === 'down' }">
                          {{ driver.tag }}{{ driver.direction === 'down' ? ' v' : ' ^' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-else class="text-white small opacity-75">
                  {{ loading_risk_panel ? 'Loading risk score from backend...' : risk_panel_message }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!action_completed" class="case-details-action-card rounded-4 p-4 mb-3 border border-purple">
          <div class="text-uppercase text-x secondary-color mb-2">Next sensible action</div>
          <h3 class="case-details-action-title mb-3">{{ sensible_action_card.title }}</h3>

          <div class="row g-3 mb-3">
            <div class="col-6 col-md-3">
              <div class="text-uppercase text-xxs secondary-color mb-1">Owner</div>
              <div class="fw-semibold text-dark">{{ owner_summary_value }}</div>
            </div>
            <div class="col-6 col-md-2">
              <div class="text-uppercase text-xxs secondary-color mb-1">By</div>
              <div class="fw-semibold text-dark">{{ sensible_action_card.by }}</div>
            </div>
            <div class="col-6 col-md-3">
              <div class="text-uppercase text-xxs secondary-color mb-1">Confidence</div>
              <div class="fw-semibold text-dark">{{ sensible_action_card.confidence }}</div>
            </div>
            <div class="col-6 col-md-4">
              <div class="text-uppercase text-xxs secondary-color mb-1">Est. exposure</div>
              <div class="fw-semibold text-dark">{{ sensible_action_card.exposure }}</div>
            </div>
          </div>

          <p class="case-details-action-copy mb-3">{{ sensible_action_card.body }}</p>

          <div v-if="sensible_action_card.actions.length" class="d-flex align-items-center gap-2 flex-wrap">
            <button
              v-for="action in sensible_action_card.actions"
              :key="action.key"
              type="button"
              class="btn rounded-small px-3"
              :class="action.recommended ? 'btn-primary-dark' : 'btn-light border'"
              @click="open_action_modal(action.key)"
            >
              {{ action.label }}
            </button>
          </div>
        </div>

        <div v-else class="case-details-action-card case-details-action-card-complete rounded-4 p-4 mb-3 border border-purple">
          <div class="text-uppercase text-x secondary-color mb-2">Next sensible action</div>
          <h3 class="case-details-action-title case-details-action-title-complete mb-3">{{ sensible_action_card.title }}</h3>
          <div class="row g-3 mb-3">
            <div class="col-6 col-md-3"><div class="text-uppercase text-xxs secondary-color mb-1">Owner</div><div class="fw-semibold text-dark">{{ owner_summary_value }}</div></div>
            <div class="col-6 col-md-2"><div class="text-uppercase text-xxs secondary-color mb-1">By</div><div class="fw-semibold text-dark">{{ sensible_action_card.by }}</div></div>
            <div class="col-6 col-md-3"><div class="text-uppercase text-xxs secondary-color mb-1">Confidence</div><div class="fw-semibold text-dark">{{ sensible_action_card.confidence }}</div></div>
            <div class="col-6 col-md-4"><div class="text-uppercase text-xxs secondary-color mb-1">Est. exposure</div><div class="fw-semibold text-dark">{{ sensible_action_card.exposure }}</div></div>
          </div>
          <p class="case-details-action-copy mb-3">{{ sensible_action_card.body }}</p>
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="case-details-paid-badge">✓ {{ sensible_action_card.recommended_action === 'payout' ? 'Paid in full' : 'Paid in full (override)' }}</span>
            <button type="button" class="btn btn-light rounded-small border px-3" :disabled="submitting_note" @click="undo_action">
              {{ submitting_note ? 'Saving...' : 'Undo' }}
            </button>
          </div>
        </div>

        <div class="case-details-reasoning-card bg-white p-4">
          <div class="d-flex align-items-center gap-2 flex-wrap border-bottom pb-3 mb-4">
            <button
              v-for="tab in display_tabs.filter((item) => item.key !== 'documents')"
              :key="tab.key"
              type="button"
              class="btn border-0 rounded-0 px-3 pb-2 case-details-tab"
              :class="{ active: active_tab === tab.key }"
              @click="set_tab(tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div v-if="active_tab === 'reasoning'" class="d-flex flex-column gap-0">
            <div v-if="loading_notes" class="small secondary-color py-3">Loading reasoning trail...</div>
            <div v-else-if="!display_reasoning_items.length" class="case-details-doc-card rounded-4 p-3 mb-3">
              <div class="fw-semibold text-dark mb-1">No reasoning trail entries yet.</div>
              <div class="small secondary-color">Notes, replies, decisions, reassignment context, and key workflow events will appear here.</div>
            </div>
            <div v-for="item in display_reasoning_items" v-else :key="item.id || `${item.date}-${item.time}-${item.body}`" class="case-details-feed-row">
              <div class="case-details-feed-time">
                <div>{{ item.date }}</div>
                <div>{{ item.time }}</div>
              </div>
              <div class="case-details-feed-body secondary-color">
                <span class="badge rounded-2 fw-normal me-2" :class="item.tag_class">{{ item.type }}</span>
                <span>{{ item.body }}</span>
              </div>
            </div>
          </div>

          <div v-else-if="active_tab === 'time'" class="d-flex flex-column gap-0">
            <div v-if="loading_time_entries" class="small secondary-color py-3">Loading time journals...</div>
            <div v-if="time_entries_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ time_entries_error }}</div>
            <div v-if="!loading_time_entries">
              <div v-if="time_entries_success" class="case-details-doc-message is-success rounded-3 px-3 py-2 mb-3">{{ time_entries_success }}</div>
              <div v-if="!display_time_entries.length" class="case-details-doc-card rounded-4 p-3 mb-3">
                <div class="fw-semibold text-dark mb-1">No time journals available for this case.</div>
              </div>
              <div v-for="entry in display_time_entries" :key="entry.id || `${entry.date}-${entry.activity}`" class="case-details-time-row">
                <div class="case-details-time-date">{{ entry.date }}</div>
                <div class="case-details-time-body">
                  <div class="fw-semibold text-dark mb-1">{{ entry.activity }}</div>
                  <div class="d-flex align-items-center gap-2 flex-wrap small secondary-color">
                    <span>{{ entry.owner }}</span>
                    <span class="badge rounded-pill fw-normal case-owner-timesheet-tag" :class="entry.billable ? 'case-owner-timesheet-tag-billable' : 'case-owner-timesheet-tag-nonbill'">
                      {{ entry.billable ? 'Billable' : 'Non-bill' }}
                    </span>
                    <span v-if="entry.evidence_count" class="badge rounded-pill fw-normal bg-light text-dark border">
                      {{ entry.evidence_count }} evidence file{{ entry.evidence_count === 1 ? '' : 's' }}
                    </span>
                  </div>
                  <div v-if="entry.evidence_count" class="mt-3">
                    <div class="case-details-time-evidence-heading">Evidence</div>
                    <div v-if="entry.evidence_files.length" class="case-details-time-evidence-list mt-2">
                      <article
                        v-for="evidence in entry.evidence_files"
                        :key="evidence.id || evidence.name"
                        class="case-details-time-evidence-row"
                      >
                        <span class="case-details-time-evidence-icon">{{ evidence.extension }}</span>
                        <span class="case-details-time-evidence-main">
                          <span class="case-details-time-evidence-name">{{ evidence.name }}</span>
                          <span class="case-details-time-evidence-meta">
                            {{ evidence.file_type }}<span v-if="evidence.size"> - {{ evidence.size }}</span> - {{ evidence.upload_status }}
                          </span>
                        </span>
                        <span class="case-details-time-evidence-actions">
                          <button
                            v-if="evidence.url"
                            type="button"
                            class="btn btn-light border rounded-pill px-3 py-1"
                            @click="open_document(evidence)"
                          >
                            Preview
                          </button>
                          <button
                            v-if="evidence.url"
                            type="button"
                            class="btn btn-light border rounded-pill px-3 py-1"
                            @click="download_document(evidence)"
                          >
                            Download
                          </button>
                        </span>
                      </article>
                    </div>
                    <div v-else class="case-details-time-evidence-empty mt-2">
                      Evidence files are attached, but the document details were not returned for this view.
                    </div>
                  </div>
                </div>
                <div class="case-details-time-hours secondary-color">{{ entry.hours }}</div>
              </div>
            </div>

            <div class="row g-2 mt-4 align-items-start">
              <div class="col-12 col-lg-5">
                <div class="text-uppercase text-xxs secondary-color mb-2">Activity</div>
                <input v-model="new_time_activity" class="form-control entity-binding-control" placeholder="e.g. Inspection brief" />
              </div>
              <div class="col-6 col-lg-2">
                <div class="text-uppercase text-xxs secondary-color mb-2">Hours</div>
                <input v-model="new_time_hours" class="form-control entity-binding-control" type="number" min="0.01" step="0.01" />
              </div>
              <div class="col-6 col-lg-2">
                <div class="text-uppercase text-xxs secondary-color mb-2">Billable?</div>
                <div class="d-flex align-items-center gap-1">
                  <button type="button" class="btn btn-light border rounded-small flex-fill" :class="{ active: new_time_billable }" @click="new_time_billable = true">Yes</button>
                  <button type="button" class="btn btn-light border rounded-small flex-fill" :class="{ active: !new_time_billable }" @click="new_time_billable = false">No</button>
                </div>
              </div>
              <div class="col-12 col-lg-3">
                <div class="text-uppercase text-xxs secondary-color mb-2">
                  Upload attachment<span v-if="time_entry_evidence_required"> *</span>
                </div>
                <input
                  ref="time_entry_evidence_input"
                  class="form-control entity-binding-control"
                  type="file"
                  multiple
                  :accept="time_entry_evidence_accept"
                  @change="handle_time_entry_evidence_change"
                >
                <div class="case-details-time-evidence-help mt-2">
                  Supported: {{ time_entry_evidence_formats_label }}. Max {{ time_entry_evidence_max_size_label }} per file.
                </div>
              </div>
              <div class="col-12 d-flex justify-content-end">
                <button type="button" class="btn btn-primary-dark rounded-small px-4" :disabled="submitting_time_entry" @click="submit_time_entry">
                  {{ submitting_time_entry ? 'Saving...' : 'Log time' }}
                </button>
              </div>
            </div>

            <div v-if="new_time_evidence_files.length" class="case-details-time-attachment-list mt-3">
              <article
                v-for="(attachment, index) in new_time_evidence_files"
                :key="attachment.key || `${time_entry_attachment_name(attachment)}-${index}`"
                class="case-details-time-attachment-row"
              >
                <span class="case-details-time-evidence-icon">{{ time_entry_attachment_file_type(attachment) }}</span>
                <span class="case-details-time-evidence-main">
                  <span class="case-details-time-evidence-name">{{ time_entry_attachment_name(attachment) }}</span>
                  <span class="case-details-time-evidence-meta">
                    {{ time_entry_attachment_file_type(attachment) }} - {{ time_entry_attachment_file_size(attachment) }} - {{ time_entry_attachment_status_label(attachment) }}
                  </span>
                </span>
                <button type="button" class="btn btn-light border rounded-pill px-3 py-1" @click="remove_time_entry_evidence(index)">Remove</button>
              </article>
            </div>

            <div class="case-details-time-footer secondary-color rounded-4 px-3 py-2 mt-3 d-flex flex-column flex-md-row justify-content-between gap-2">
              <div><strong>{{ time_journal_summary.billable_hours }}h billable</strong> - {{ time_journal_summary.billable_amount }} at handler rate - <strong>{{ time_journal_summary.non_billable_hours }}h non-billable</strong></div>
              <div class="text-uppercase text-xxs secondary-color">Rolls up to weekly timesheet</div>
            </div>
          </div>

          <div v-else-if="active_tab === 'notes'" class="d-flex flex-column gap-3">
            <div v-if="loading_notes" class="small secondary-color py-3">Loading notes...</div>
            <div v-if="notes_error" class="case-details-doc-message is-error rounded-3 px-3 py-2">{{ notes_error }}</div>
            <div v-if="notes_success" class="case-details-doc-message is-success rounded-3 px-3 py-2">{{ notes_success }}</div>

            <div>
              <div class="text-uppercase text-xxs secondary-color mb-2">Add a note</div>
              <div v-if="note_mention_options.length" class="case-details-note-mention-row mb-2">
                <select
                  v-model="selected_note_mention_key"
                  class="form-select entity-binding-control case-details-note-mention-select"
                  aria-label="Mention someone"
                  @change="insert_note_mention"
                >
                  <option value="">Mention someone...</option>
                  <option
                    v-for="person in note_mention_options"
                    :key="person.key"
                    :value="person.key"
                  >
                    {{ person.name }}<template v-if="person.detail"> - {{ person.detail }}</template>
                  </option>
                </select>
              </div>
              <div class="case-details-note-composer">
                <div
                  v-if="new_note_body"
                  class="case-details-note-highlight-layer"
                  aria-hidden="true"
                >
                  <span
                    v-for="(segment, segment_index) in new_note_body_segments"
                    :key="`composer-note-${segment_index}`"
                    :class="{ 'case-details-note-mention-text': segment.is_mention }"
                  >{{ segment.text }}</span>
                </div>
                <textarea
                  ref="note_body_input"
                  v-model="new_note_body"
                  class="form-control entity-binding-control case-details-note-textarea"
                  :class="{ 'case-details-note-textarea-has-highlight': new_note_body }"
                  placeholder="Internal note - type @ to mention someone"
                  spellcheck="false"
                  @input="handle_note_body_input"
                  @click="handle_note_body_cursor_change"
                  @keyup="handle_note_body_cursor_change"
                  @keydown="handle_note_body_keydown"
                  @blur="close_note_mention_menu"
                ></textarea>
                <div
                  v-if="note_mention_menu_open && filtered_note_mention_options.length"
                  class="case-details-note-mention-menu"
                >
                  <button
                    v-for="person in filtered_note_mention_options"
                    :key="`inline-${person.key}`"
                    type="button"
                    class="case-details-note-mention-option"
                    @mousedown.prevent="select_inline_note_mention(person)"
                  >
                    <span class="case-details-note-mention-avatar">{{ person.name.slice(0, 2).toUpperCase() }}</span>
                    <span class="case-details-note-mention-main">
                      <span class="case-details-note-mention-name">{{ person.name }}</span>
                      <span v-if="person.detail" class="case-details-note-mention-detail">{{ person.detail }}</span>
                    </span>
                  </button>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-2">
                <button
                  type="button"
                  class="btn btn-primary-dark rounded-small px-3"
                  :disabled="submitting_note"
                  @click="submit_note"
                >
                  {{ submitting_note ? 'Posting...' : 'Post note' }}
                </button>
              </div>
            </div>

            <div v-if="!loading_notes" class="case-details-flow">
              <div v-if="!display_notes.length" class="case-details-doc-card rounded-4 p-3">
                <div class="fw-semibold text-dark mb-1">No notes found</div>
                <div class="small secondary-color">Internal notes will appear here.</div>
              </div>

              <div
                v-for="thread in display_note_threads"
                :key="thread.key"
                class="case-details-note-thread"
                :class="{ 'has-replies': thread.replies.length }"
              >
                <article class="case-details-doc-card rounded-4 p-3 case-details-note-card">
                  <div class="d-flex justify-content-between gap-3 mb-2">
                    <div>
                      <div v-if="thread.note.is_reply" class="case-details-note-context-label">
                        <i class="bi bi-reply"></i>
                        Replying to {{ thread.note.reply_to }}
                      </div>
                      <div v-else-if="thread.note.mentioned_name" class="case-details-note-context-label is-mention">
                        @ Mentioned {{ thread.note.mentioned_name }}
                      </div>
                      <div class="fw-semibold text-dark">{{ thread.note.author }}</div>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        class="btn btn-light btn-sm rounded-small border case-details-note-reply-btn"
                        @click="start_note_reply(thread.note)"
                      >
                        Reply
                      </button>
                      <div class="small secondary-color">{{ thread.note.timestamp }}</div>
                    </div>
                  </div>

                  <div class="text-dark">
                    <span
                      v-for="(segment, segment_index) in thread.note.body_segments"
                      :key="`${thread.note.id || thread.note.author}-${segment_index}`"
                      :class="{ 'case-details-note-mention-text': segment.is_mention }"
                    >{{ segment.text }}</span>
                  </div>

                  <div v-if="replying_note_id === (thread.note.id || `${thread.note.author}-${thread.note.timestamp}`)" class="case-details-note-reply-box">
                    <div class="case-details-note-original-preview">
                      <div class="case-details-note-original-author">Replying to {{ thread.note.author }}</div>
                      <div>{{ thread.note.body }}</div>
                    </div>
                    <textarea
                      v-model="note_reply_body"
                      class="form-control entity-binding-control case-details-note-reply-textarea"
                      :placeholder="`Reply to ${thread.note.author}...`"
                      :disabled="submitting_note_reply"
                    ></textarea>
                    <div class="d-flex justify-content-end gap-2 mt-2">
                      <button
                        type="button"
                        class="btn btn-light btn-sm rounded-small border"
                        :disabled="submitting_note_reply"
                        @click="cancel_note_reply"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="btn btn-primary-dark btn-sm rounded-small"
                        :disabled="submitting_note_reply"
                        @click="submit_note_reply(thread.note)"
                      >
                        {{ submitting_note_reply ? 'Posting...' : 'Post reply' }}
                      </button>
                    </div>
                  </div>
                </article>

                <div v-if="thread.replies.length" class="case-details-note-reply-list">
                  <article
                    v-for="reply in thread.replies"
                    :key="reply.thread_key"
                    class="case-details-doc-card rounded-4 p-3 case-details-note-card case-details-note-reply-card"
                  >
                    <div class="d-flex justify-content-between gap-3 mb-2">
                      <div>
                        <div class="case-details-note-context-label">
                          <i class="bi bi-reply"></i>
                          Replying to {{ reply.reply_to }}
                        </div>
                        <div class="fw-semibold text-dark">{{ reply.author }}</div>
                      </div>
                      <div class="d-flex align-items-center gap-2">
                        <button
                          type="button"
                          class="btn btn-light btn-sm rounded-small border case-details-note-reply-btn"
                          @click="start_note_reply(reply)"
                        >
                          Reply
                        </button>
                        <div class="small secondary-color">{{ reply.timestamp }}</div>
                      </div>
                    </div>

                    <div class="case-details-note-original-preview is-compact">
                      <div class="case-details-note-original-author">{{ thread.note.author }}</div>
                      <div>{{ thread.note.body }}</div>
                    </div>

                    <div class="text-dark">
                      <span
                        v-for="(segment, segment_index) in reply.body_segments"
                        :key="`${reply.id || reply.author}-${segment_index}`"
                        :class="{ 'case-details-note-mention-text': segment.is_mention }"
                      >{{ segment.text }}</span>
                    </div>

                    <div v-if="replying_note_id === (reply.id || `${reply.author}-${reply.timestamp}`)" class="case-details-note-reply-box">
                      <div class="case-details-note-original-preview">
                        <div class="case-details-note-original-author">Replying to {{ reply.author }}</div>
                        <div>{{ reply.body }}</div>
                      </div>
                      <textarea
                        v-model="note_reply_body"
                        class="form-control entity-binding-control case-details-note-reply-textarea"
                        :placeholder="`Reply to ${reply.author}...`"
                        :disabled="submitting_note_reply"
                      ></textarea>
                      <div class="d-flex justify-content-end gap-2 mt-2">
                        <button
                          type="button"
                          class="btn btn-light btn-sm rounded-small border"
                          :disabled="submitting_note_reply"
                          @click="cancel_note_reply"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          class="btn btn-primary-dark btn-sm rounded-small"
                          :disabled="submitting_note_reply"
                          @click="submit_note_reply(reply)"
                        >
                          {{ submitting_note_reply ? 'Posting...' : 'Post reply' }}
                        </button>
                      </div>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="table-responsive">
            <div v-if="loading_audit_logs" class="small secondary-color py-3">Loading audit log...</div>
            <div v-else-if="audit_logs_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ audit_logs_error }}</div>
            <div v-else-if="!display_audit_rows.length" class="case-details-doc-card rounded-4 p-3 mb-3">
              <div class="fw-semibold text-dark mb-1">No audit logs available for this case.</div>
            </div>
            <table v-else class="table align-middle mb-0">
              <tbody>
                <tr v-for="row in display_audit_rows" :key="row.id || `${row.when}-${row.actor}`">
                  <td class="px-0 py-3 case-details-audit-when">{{ row.when }}</td>
                  <td class="px-0 py-3 case-details-audit-actor">{{ row.actor }}</td>
                  <td class="px-0 py-3 case-details-audit-event">{{ row.event }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="d-none">
        <div class="case-details-service-card bg-white p-4 mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div class="text-uppercase text-x fw-bold secondary-color">Service levels</div>
            <div v-if="loading_sla_runtimes" class="text-uppercase text-xxs secondary-color">Loading</div>
          </div>

          <div v-if="sla_runtimes_error" class="case-details-doc-message is-error rounded-3 px-3 py-2">{{ sla_runtimes_error }}</div>
          <div v-else-if="!loading_sla_runtimes && !display_sla_runtimes.length" class="small secondary-color">
            No SLA clock has started for this case.
          </div>
          <div v-else class="d-flex flex-column gap-2">
            <article
              v-for="runtime in display_sla_runtimes"
              :key="runtime.id"
              class="case-details-sla-row rounded-3 p-3"
              :class="runtime.tone_class"
            >
              <div class="d-flex align-items-start justify-content-between gap-3">
                <div>
                  <div class="fw-semibold text-dark">{{ runtime.name }}</div>
                  <div class="small secondary-color mt-1">
                    Due {{ runtime.due_label || 'not scheduled' }}
                    <span v-if="runtime.workflow_status_name"> · {{ runtime.workflow_status_name }}</span>
                  </div>
                </div>
                <span class="badge rounded-pill case-details-sla-status">{{ runtime.status_label }}</span>
              </div>
              <div class="case-details-sla-time fw-semibold mt-2">{{ runtime.time_label }}</div>
            </article>
          </div>
        </div>

        <div class="case-details-risk-panel p-4 mb-3">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div class="text-uppercase text-x text-white opacity-75">Verdict - risk score</div>
            <div v-if="loading_risk_panel" class="text-uppercase text-xxs text-white opacity-75">Loading</div>
          </div>

          <div v-if="has_risk_panel" class="case-details-flow">
            <div class="case-details-risk-score mb-3">{{ risk_panel.score }}<span>/100</span></div>
            <div v-if="display_risk_band" class="badge rounded-2 fw-normal case-details-risk-badge mb-3">{{ display_risk_band }}</div>

            <div class="progress case-details-risk-progress mb-4">
              <div class="progress-bar" role="progressbar" :style="{ width: risk_progress_width }"></div>
            </div>

            <div class="text-uppercase text-x text-white opacity-75 mb-3">Key drivers</div>
            <div v-if="!display_risk_drivers.length" class="text-white small opacity-75">
              No key drivers were returned by the backend for this score.
            </div>
            <div v-else class="d-flex flex-column gap-3">
              <div v-for="driver in display_risk_drivers" :key="driver.label" class="case-details-risk-driver">
                <div class="d-flex align-items-center justify-content-between gap-3">
                  <div class="d-flex align-items-center gap-3">
                    <span class="case-details-risk-delta">{{ driver.delta }}</span>
                    <span class="min-w-0">
                      <span class="d-block text-white">{{ driver.question }}</span>
                      <span class="d-block text-white small opacity-75">{{ driver.answer }}</span>
                    </span>
                  </div>
                  <span class="badge rounded-2 fw-normal case-details-risk-driver-tag" :class="{ 'is-down': driver.direction === 'down' }">
                    {{ driver.tag }}{{ driver.direction === 'down' ? ' v' : ' ^' }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-white small opacity-75">
            {{ loading_risk_panel ? 'Loading risk score from backend...' : risk_panel_message }}
          </div>
        </div>

      </div>
    </section>
  </div>

  <div v-if="show_document_preview" class="case-details-document-preview-backdrop" @click.self="close_document_preview">
    <aside class="case-details-document-preview bg-white">
      <header class="case-details-document-preview-header">
        <div class="case-details-document-preview-top">
          <div class="min-w-0 case-details-document-preview-copy">
            <h2 class="case-details-document-preview-title mb-1">{{ selected_document.name }}</h2>
            <div class="case-details-document-meta">
              {{ selected_document.extension }}<span v-if="selected_document.size"> - {{ selected_document.size }}</span><span v-if="selected_document.meta"> - {{ selected_document.meta }}</span>
            </div>
          </div>
          <button type="button" class="case-details-document-close btn border-0 p-0" aria-label="Close document preview" @click="close_document_preview">
            &times;
          </button>
        </div>

        <div class="case-details-document-preview-actions">
          <div class="case-details-document-preview-status-wrap">
            <div class="case-details-document-preview-status-label">Review status</div>
            <span
              v-if="selected_document.verification_label"
              class="badge rounded-pill fw-normal case-details-document-preview-status"
              :class="selected_document.verification_state === 'verified'
                ? 'bg-success-subtle text-success-emphasis'
                : selected_document.verification_state === 'rejected'
                  ? 'bg-danger-subtle text-danger-emphasis'
                  : 'bg-warning-subtle text-warning-emphasis'"
            >
              {{ selected_document.verification_label }}
            </span>
          </div>
          <button
            v-if="selected_document.url"
            type="button"
            class="btn btn-light rounded-pill border px-3"
            @click="download_document(selected_document)"
          >
            Download
          </button>
        </div>
      </header>

      <div class="case-details-document-preview-body">
        <div v-if="can_preview_document(selected_document)" class="case-details-document-preview-frame">
          <img
            v-if="is_image_document(selected_document)"
            :src="selected_document.url"
            :alt="selected_document.name"
            class="case-details-document-preview-image"
          >
          <iframe
            v-else-if="is_pdf_document(selected_document)"
            :src="selected_document.url"
            :title="selected_document.name"
            class="case-details-document-preview-iframe"
          ></iframe>
          <video
            v-else-if="is_video_document(selected_document)"
            :src="selected_document.url"
            class="case-details-document-preview-media"
            controls
          ></video>
          <audio
            v-else-if="is_audio_document(selected_document)"
            :src="selected_document.url"
            class="case-details-document-preview-audio"
            controls
          ></audio>
        </div>
        <div v-else class="case-details-document-preview-empty rounded-4 border">
          This document cannot be previewed in the browser. Use Download to open it.
        </div>
      </div>

      <footer class="case-details-document-preview-footer">
        <transition name="case-details-document-review-fade">
          <div
            v-if="document_review_mode === 'reject' && can_review_documents && can_review_document(selected_document)"
            class="case-details-document-review-panel"
          >
            <div class="case-details-document-review-panel-header">
              <div>
                <div class="case-details-document-preview-status-label">Rejection reason</div>
                <div class="small secondary-color mt-1">Tell the claimant why this document cannot be accepted.</div>
              </div>
            </div>

            <textarea
              id="document_review_comment"
              v-model="document_review_comment"
              class="form-control case-details-document-review-textarea mt-3"
              rows="4"
              placeholder="Write why you are rejecting this document"
              :disabled="submitting_document_review"
            ></textarea>

            <div class="d-flex justify-content-start justify-content-md-end gap-2 mt-3 flex-wrap">
              <button
                type="button"
                class="btn btn-light rounded-pill border px-3"
                :disabled="submitting_document_review"
                @click="cancel_document_rejection"
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-danger rounded-pill px-3"
                :disabled="submitting_document_review"
                @click="review_document(selected_document, false)"
              >
                {{ submitting_document_review ? 'Saving...' : 'Submit rejection' }}
              </button>
            </div>
          </div>
        </transition>

        <div
          v-if="can_review_documents && can_review_document(selected_document)"
          class="case-details-document-preview-buttons case-details-document-preview-footer-buttons"
        >
          <button
            type="button"
            class="btn btn-outline-danger rounded-pill px-3"
            :disabled="submitting_document_review"
            @click="start_document_rejection(selected_document)"
          >
            Reject
          </button>
          <button
            type="button"
            class="btn btn-primary-dark rounded-pill px-3"
            :disabled="submitting_document_review"
            @click="review_document(selected_document, true)"
          >
            {{ submitting_document_review ? 'Saving...' : 'Verify' }}
          </button>
        </div>
      </footer>
    </aside>
  </div>

  <div v-if="action_decision_modal" class="case-details-decision-backdrop" @click.self="close_action_modal">
    <section class="case-details-decision-modal case-details-action-modal bg-white">
      <header class="case-details-decision-header">
        <div class="min-w-0">
          <h2 class="case-details-decision-title mb-2">{{ selected_sensible_action.modal_title }}</h2>
          <div class="case-details-decision-subtitle">{{ display_case_title }} · {{ display_case_code }}</div>
        </div>
        <button type="button" class="case-details-decision-close btn border-0 p-0" aria-label="Close action modal" @click="close_action_modal">×</button>
      </header>
      <div class="case-details-decision-body">
        <div v-if="notes_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ notes_error }}</div>
        <p :class="payout_action_is_override ? 'case-details-action-modal-warning' : 'case-details-action-modal-copy'">{{ selected_sensible_action.modal_body }}</p>
        <label class="case-details-decision-label text-uppercase mt-3" for="action_decision_reason">Notes for audit trail</label>
        <textarea id="action_decision_reason" v-model="action_decision_reason" class="form-control case-details-decision-textarea" placeholder="Optional reason or context..." :disabled="submitting_note"></textarea>
      </div>
      <footer class="case-details-decision-footer">
        <button type="button" class="btn btn-light rounded-small border px-4" :disabled="submitting_note" @click="close_action_modal">Cancel</button>
        <button type="button" class="btn rounded-small px-4" :disabled="submitting_note" :class="payout_action_is_override ? 'btn-danger-dark' : 'btn-primary-dark'" @click="confirm_action">
          {{ submitting_note ? 'Saving...' : selected_sensible_action.confirm_label }}
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_decision_modal" class="case-details-decision-backdrop" @click.self="close_decision_modal">
    <section class="case-details-decision-modal bg-white">
      <header class="case-details-decision-header">
        <div class="min-w-0">
          <h2 class="case-details-decision-title mb-2">Decide &middot; {{ display_case_code }}</h2>
          <div class="case-details-decision-subtitle">{{ decision_modal_subtitle }}</div>
        </div>
        <button type="button" class="case-details-decision-close btn border-0 p-0" aria-label="Close decision modal" @click="close_decision_modal">
          &times;
        </button>
      </header>

      <div class="case-details-decision-body">
        <div v-if="decision_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ decision_error }}</div>

        <div class="case-details-decision-label text-uppercase">Workflow status</div>

        <div v-if="!loading_decision_workflow" class="case-details-workflow-status-list mb-3">
          <div class="case-details-workflow-status is-current">
            <span class="case-details-workflow-marker"></span>
            <span class="case-details-workflow-name">{{ current_status_label || 'Unknown' }}</span>
            <span class="case-details-decision-badge">Current</span>
          </div>
        </div>

        <div v-if="!loading_decision_workflow" class="case-details-flow">
          <div class="case-details-decision-label text-uppercase">Choose An Outcome</div>

          <div v-if="decision_is_final_status" class="case-details-doc-card rounded-4 p-3 mb-3">
            <div class="fw-semibold text-dark mb-1">No further transitions available.</div>
            <div class="small secondary-color">This case is at the final workflow status.</div>
          </div>

          <div v-else-if="!valid_next_statuses.length" class="case-details-doc-card rounded-4 p-3 mb-3">
            <div class="fw-semibold text-dark mb-1">No valid next statuses.</div>
            <div class="small secondary-color">The workflow does not allow a transition from the current status.</div>
          </div>

          <div v-else class="d-flex flex-column gap-2">
          <label
            v-for="status in valid_next_statuses"
            :key="status.id"
            class="case-details-decision-option"
            :class="{ 'is-selected': Number(selected_decision_status_id) === Number(status.id) }"
          >
            <input
              v-model="selected_decision_status_id"
              class="case-details-decision-radio"
              type="radio"
              name="case_decision_status"
              :value="String(status.id)"
            >
            <span class="case-details-decision-radio-ui"></span>
            <span class="min-w-0">
              <span class="case-details-decision-option-title">
                {{ status.name }}
              </span>
              <span class="case-details-decision-option-body">Valid next status from {{ current_status_label }}.</span>
            </span>
          </label>
          </div>

          <label class="case-details-decision-label text-uppercase mt-3" for="case_decision_reason">
            Reasoning trail entry
          </label>
          <textarea
            id="case_decision_reason"
            v-model="decision_reason"
            class="form-control case-details-decision-textarea"
            placeholder="Briefly explain your decision for the reasoning trail..."
            :disabled="submitting_decision"
          ></textarea>
        </div>
      </div>

      <footer class="case-details-decision-footer">
        <button type="button" class="btn btn-light rounded-small border px-4" :disabled="submitting_decision" @click="close_decision_modal">Cancel</button>
        <button
          type="button"
          class="btn btn-primary-dark rounded-small px-4"
          :disabled="loading_decision_workflow || submitting_decision || !selected_decision_status"
          @click="confirm_decision"
        >
          {{ submitting_decision ? 'Updating...' : 'Confirm status' }}
        </button>
      </footer>
    </section>
  </div>

  <div v-if="show_reassign_modal" class="case-details-decision-backdrop" @click.self="close_reassign_modal">
    <section class="case-details-decision-modal case-details-reassign-modal bg-white">
      <header class="case-details-decision-header">
        <div class="min-w-0">
          <h2 class="case-details-decision-title mb-2">Reassign {{ display_case_code }}</h2>
          <div class="case-details-decision-subtitle">Current owner &middot; {{ owner_summary_value }}</div>
        </div>
        <button type="button" class="case-details-decision-close btn border-0 p-0" aria-label="Close reassign modal" @click="close_reassign_modal">
          &times;
        </button>
      </header>

      <div class="case-details-decision-body">
        <label class="case-details-decision-label text-uppercase" for="case_reassign_search">Search reviewers</label>
        <input
          id="case_reassign_search"
          v-model="reassign_search"
          class="form-control case-details-reassign-search"
          placeholder="Name, role, email..."
        >

        <div v-if="reassign_error" class="case-details-doc-message is-error rounded-3 px-3 py-2 mb-3">{{ reassign_error }}</div>
        <div v-if="reassign_success" class="case-details-doc-message is-success rounded-3 px-3 py-2 mb-3">{{ reassign_success }}</div>

        <div class="case-details-reassign-list">
          <div v-if="loading_reassign_users" class="small secondary-color py-3">Loading users...</div>
          <button
            v-else
            v-for="member in filtered_reassign_panel_members"
            :key="member.key"
            type="button"
            class="case-details-reassign-member"
            :class="{ 'is-selected': selected_reassign_users.includes(member.key) }"
            @click="toggle_reassign_user(member)"
          >
            <span class="case-details-reassign-avatar" :class="member.avatar_class">{{ member.initials }}</span>
            <span class="case-details-reassign-main">
              <span class="case-details-reassign-name">{{ member.name }}</span>
              <span class="case-details-reassign-detail">{{ member.detail }}</span>
            </span>
            <span class="case-details-reassign-badge" :class="member.badge_class">{{ member.badge }}</span>
          </button>

          <div v-if="!loading_reassign_users && !filtered_reassign_panel_members.length" class="case-details-doc-card rounded-4 p-3">
            <div class="fw-semibold text-dark mb-1">No workflow reviewers available for reassignment.</div>
            <div class="small secondary-color">Try a different name, role, or email.</div>
          </div>
        </div>

        <label class="case-details-decision-label text-uppercase mt-3" for="case_reassign_note">
          Note to new handler
        </label>
        <textarea
          id="case_reassign_note"
          v-model="reassign_note"
          class="form-control case-details-decision-textarea"
          placeholder="Context for the new owner..."
        ></textarea>
      </div>

      <footer class="case-details-decision-footer">
        <button type="button" class="btn btn-light rounded-small border px-4" @click="close_reassign_modal">Cancel</button>
        <button type="button" class="btn btn-primary-dark rounded-small px-4" :disabled="submitting_reassign" @click="confirm_reassign">
          {{ submitting_reassign ? 'Reassigning...' : 'Reassign case' }}
        </button>
      </footer>
    </section>
  </div>
</div>
